/**
 * useGroupsData hook
 * Processes groups data: separates into ACTIVE and PENDING types
 * Returns a unified array compatible with FriendListItem format
 */

import { useMemo } from 'react'
import type { Group } from '../types/groups.types'
import type { FriendListItem } from './useFriendData'
import { buildListItems } from '../helpers/buildListItems.helper'
import {
  GROUP_LIST_ITEM_TYPE,
  INVITATION_STATUS,
  type GroupListItemType,
} from '../constants/groups.constants'

export type GroupListItem = {
  type: GroupListItemType
  group: Group
  id: string
}

export type UnifiedListItem = FriendListItem | GroupListItem

interface UseGroupsDataProps {
  groups: Group[]
  duplicateCount?: number
  /** @default 1 */
}

interface UseGroupsDataReturn {
  items: GroupListItem[]
}

/**
 * Hook to process groups data for display
 * Excludes sent invitations (only shows groups I'm in + invites I received).
 * Separates into GROUP_PENDING (received) and GROUP_ACTIVE (accepted).
 * Orders items: GROUP_PENDING (invites I got) first, then GROUP_ACTIVE (groups I'm in).
 */
export const useGroupsData = ({
  groups,
  duplicateCount = 1,
}: UseGroupsDataProps): UseGroupsDataReturn => {
  const items = useMemo(() => {
    const visible = groups.filter(
      (g) =>
        g.invitationStatus === INVITATION_STATUS.ACCEPTED ||
        !g.invitationStatus ||
        (g.invitationStatus === INVITATION_STATUS.PENDING && g.invitationType === 'received')
    )
    return buildListItems<Group, GroupListItem>({
      items: visible,
      getCategory: (g) =>
        g.invitationStatus === INVITATION_STATUS.ACCEPTED || !g.invitationStatus
          ? GROUP_LIST_ITEM_TYPE.GROUP_ACTIVE
          : GROUP_LIST_ITEM_TYPE.GROUP_PENDING,
      categoryOrder: [
        GROUP_LIST_ITEM_TYPE.GROUP_PENDING,
        GROUP_LIST_ITEM_TYPE.GROUP_ACTIVE,
      ],
      duplicateCount,
      getStableId: (g) => g.id,
      toListItem: (group, type, listItemId, entityId, dupIndex, dupSubIndex) => ({
        type: type as GroupListItem['type'],
        group:
          duplicateCount > 1 && dupIndex !== undefined && dupSubIndex !== undefined
            ? {
                ...group,
                id: entityId,
                invitationId: group.invitationId
                  ? `${group.invitationId}-dup-${dupIndex}-${dupSubIndex}`
                  : group.invitationId,
              }
            : group,
        id: listItemId,
      }),
    })
  }, [groups, duplicateCount])
  return { items }
}
